import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";
import { deviceTrustGeneration, markDeviceTrusted } from "../utils/device-trust";

export { ApiError as OnboardingApiError } from "./api.errors";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface OnboardingStatusData {
  onboarding_status: string;
  user_id: string;
  clinic_id: string | null;
}

export interface ClinicData {
  clinic_id: string;
  name: string;
  location: string;
  company_size?: string;
  website?: string;
  address?: string;
  phone?: string;
  email?: string;
  logo_url?: string | null;
}

export interface DocumentData {
  document_id: string;
  document_type: string;
  file_name: string;
  file_path: string;
}

export interface DocumentUrlsData {
  doc_incorporation_url: string | null;
  doc_gst_url: string | null;
  doc_clinic_registration_url: string | null;
  doc_msme_url: string | null;
  doc_other_regulatory_url: string | null;
}

export interface PresignedUrlEntry {
  upload_url: string | null;  // null in dev mode — skip actual S3 upload
  s3_key: string;
}

export interface DocumentPresignedUrlSet {
  doc_incorporation: PresignedUrlEntry;
  doc_gst: PresignedUrlEntry;
  doc_clinic_registration: PresignedUrlEntry;
  doc_msme: PresignedUrlEntry;
  doc_other_regulatory: PresignedUrlEntry;
  s3_enabled: boolean;
}

/** GET /auth/me — the session's identity plus its derived capabilities. */
export interface MeResponse {
  role: string;
  user_id?: string | null;
  doctor_id?: string | null;
  /** Credential gate (verified medical registration + credentialed clinic). */
  can_prescribe?: boolean;
  clinic_credentialed?: boolean;
  /**
   * Effective RBAC grants in the acting clinic. Advisory — it shapes the UI so
   * a user isn't shown a page whose GET would 403; the server re-checks every
   * request. Absent on an older backend, which usePermissions treats as
   * "unknown" and falls back to the role, never as "no permissions".
   */
  permission_codes?: string[];
}

// ── API ────────────────────────────────────────────────────────────────────────

export const OnboardingAPI = {
  setRole(role: string): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(ROUTES.AUTH_ROLE, {
      method: "POST",
      body: JSON.stringify({ role }),
    });
  },

  /** Also releases the post-sign-in work waiting on device trust (utils/device-trust). */
  async getMe(): Promise<MeResponse> {
    const sentInGeneration = deviceTrustGeneration();
    const me = await apiRequest<MeResponse>(ROUTES.AUTH_ME);
    markDeviceTrusted(sentInGeneration);
    return me;
  },

  getStatus(): Promise<OnboardingStatusData> {
    return apiRequest<OnboardingStatusData>(ROUTES.ONBOARDING_STATUS);
  },

  getClinic(): Promise<ClinicData> {
    return apiRequest<ClinicData>(ROUTES.ONBOARDING_CLINIC);
  },

  getClinicById(clinicId: string): Promise<ClinicData> {
    return apiRequest<ClinicData>(ROUTES.CLINIC_BY_ID(clinicId));
  },

  saveClinic(data: { name: string; location: string; company_size?: string; website?: string }): Promise<ClinicData> {
    return apiRequest<ClinicData>(ROUTES.ONBOARDING_CLINIC, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateClinic(data: { name: string; location: string; company_size?: string; website?: string }): Promise<ClinicData> {
    return apiRequest<ClinicData>(ROUTES.ONBOARDING_CLINIC, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  getPresignedUrls(): Promise<DocumentPresignedUrlSet> {
    return apiRequest<DocumentPresignedUrlSet>(ROUTES.ONBOARDING_DOCS_PRESIGNED);
  },

  submitDocuments(uploadedKeys: Record<string, string> = {}): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(ROUTES.ONBOARDING_DOCS, {
      method: "POST",
      body: JSON.stringify({ uploaded_keys: uploadedKeys }),
    });
  },

  verifyEmail(): Promise<OnboardingStatusData> {
    return apiRequest<OnboardingStatusData>(ROUTES.ONBOARDING_VERIFY_EMAIL, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  recordConsentDetails(payload: {
    screen_width: number;
    screen_height: number;
    method: "email_signup" | "google_oauth" | "apple_oauth";
    client_tos_version: string;
    client_pp_version: string;
  }): Promise<void> {
    return apiRequest<void>(ROUTES.ONBOARDING_CONSENT, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  recordAiScribeConsent(consented: boolean): Promise<{ consented: boolean }> {
    return apiRequest<{ consented: boolean }>("/onboarding/ai-scribe-consent", {
      method: "POST",
      body: JSON.stringify({ consented }),
    });
  },

  listDocuments(): Promise<DocumentUrlsData> {
    return apiRequest<DocumentUrlsData>(ROUTES.ONBOARDING_DOCS);
  },

  skipDocuments(): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(`${ROUTES.ONBOARDING_DOCS}/skip`, {
      method: "POST",
    });
  },

  getDocumentViewUrl(docField: string): Promise<{ view_url: string }> {
    return apiRequest<{ view_url: string }>(
      `/onboarding/documents/view-url?doc_field=${encodeURIComponent(docField)}`
    );
  },
};
